import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../../../../components/Modal';
import styles from './modalPayment.module.scss';
import { CircularProgress } from '@mui/material';
import { InitialContent } from './components/InitialContent';
import { PaymentContent } from './components/PaymentContent';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';

interface FormDataType {
    plate?: string;
    rut?: string;
    email?: string;
    [key: string]: any;
}

interface ModalPaymentProps {
    open: boolean;
    onClose: () => void;
    handlePaymentProcess: () => void;
    dataPayment?: string;
    url?: string;
    user?: any;
    carInfo?: any;
    formData?: FormDataType;
    linkPayment?: any;
    reloadClose: boolean;
}

const ModalPayment: React.FC<ModalPaymentProps> = ({
    open,
    onClose,
    handlePaymentProcess,
    dataPayment,
    url,
    user,
    carInfo,
    linkPayment,
    formData,
    reloadClose
}) => {
    const [isPolling, setIsPolling] = useState(false);
    const [isPaymentCancelled, setIsPaymentCancelled] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout>();
    const paymentWindowRef = useRef<Window | null>(null);

    const checkWindowStatus = () => {
        if (paymentWindowRef.current) {
            if (paymentWindowRef.current.closed) {
                setIsPaymentCancelled(true);
                stopPolling();
                handlePaymentProcess();
            }
        }
    };
    
    const startPolling = () => {
        setIsPolling(true);
        intervalRef.current = setInterval(() => {
            checkWindowStatus();
            handlePaymentProcess();
        }, 10000);
    };

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }
        setIsPolling(false);
    };

    const handleCloseClick = () => {
        stopPolling();
        onClose();
        if (reloadClose) {
            window.location.reload();
        }
    };


    const handlePaymentClick = () => {
        if (url) {
            handlePaymentProcess();
            paymentWindowRef.current = window.open(url, '_blank');
            if (paymentWindowRef.current) {
                paymentWindowRef.current.focus();
                startPolling();
            } else {
                console.log('Failed to open payment window - popup might be blocked');
            }
        }
    };

    const getPaymentStatusContent = () => {
        switch (dataPayment) {
            case 'pending':
            case 'processing':
                return (
                    <>
                        <CircularProgress color="inherit" className={styles.processingIcon} />
                        <p className={styles.statusText}>EN PROCESO...</p>
                    </>
                );
            case 'approved':
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClick}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <TaskAltOutlinedIcon className={styles.aceptIcon} />
                        <p className={`${styles.statusText} ${styles.approved}`}>PAGO APROBADO</p>
                        <p className={`${styles.statusText} ${styles.approvedSub}`}>Recibiras el informe por correo en unos minutos...</p>
                    </>
                );
            case 'cancelled':
            case 'rejected':
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClick}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <CancelOutlinedIcon className={styles.cancelIcon} />
                        <p className={`${styles.statusText} ${styles.cancelled}`}>PAGO CANCELADO</p>
                    </>
                );
            default:
                return (
                    <>
                        <CircularProgress color="inherit" className={styles.processingIcon} />
                        <p className={styles.statusText}>EN PROCESO...</p>
                    </>
                );
        }
    };

    useEffect(() => {
        return () => {
            stopPolling();
            if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
                console.log('Closing payment window on unmount');
                paymentWindowRef.current.close();
            }
        };
    }, []);

    return (
        <>
            <Modal
                variant=""
                open={open}
                onClose={() => { }}
                className={styles.modal}
            >
                {linkPayment?.fetching === "loading" ? (
                    <div className={styles.loaderContainer}>
                        <CircularProgress style={{ color: '#7000FF' }} />
                    </div>
                ) : dataPayment ? (
                    <PaymentContent
                        handleCloseClick={handleCloseClick}
                        isPaymentCancelled={isPaymentCancelled}
                        getPaymentStatusContent={getPaymentStatusContent}
                    />
                ) : (
                    <InitialContent
                        handleCloseClick={handleCloseClick}
                        handlePaymentClick={handlePaymentClick}
                        url={url}
                        user={user}
                        carInfo={carInfo}
                        formData={formData}
                    />
                )}
            </Modal>
        </>
    );
};

export default ModalPayment;
